import React from 'react';
import EventBrellaLayout from './EventBrellaLayout';

const FAQ_SECTIONS = [
  {
    title: 'General Platform',
    items: [
      {
        q: 'What are the biggest advantages of EventBrella over Eventbrite?',
        a: [
          'Your guest data (email + phone) belongs to you, not the platform. Returning guests are recognized at the door and bar. Ticket, lane/table reservation, and on-site ordering all run through one QR code and one checkout — no separate parking or ordering apps.',
          "Beyond the platform itself, we offer partnership opportunities where organizations build out their own digital platform and customer base — adding a new revenue stream, branding power, and name recognition. Our goal is for organizations like Strike After Dark to be seen not just as event planners, but as hospitality professionals curating a seamless guest experience. Instead of playing in Eventbrite's arena, we're building our own — with true ownership of the guest experience.",
        ],
      },
      {
        q: 'What limitations should we be aware of compared to Eventbrite?',
        a: [
          "Eventbrite has broad public event discovery/marketplace traffic built from years of scale. EventBrella does not currently offer that discovery layer — ticket sales are driven by your own marketing to your database. That said, your database grows as you grow, and Eventbrite's role stops at printing tickets — they're the ushers. We're the event managers, aiming to improve every event based on real-time data and revenue, using technology built for where hospitality is headed, not where it was a decade ago.",
        ],
      },
      {
        q: 'Is EventBrella designed to replace Eventbrite completely, or do you recommend using both together?',
        a: [
          'Designed to replace it — EventBrella covers ticketing, reservations, on-site ordering, and guest data ownership in one system.',
        ],
      },
    ],
  },
  {
    title: 'Ticketing & Sales',
    items: [
      { q: 'Can we create promo codes, discount codes, and early-bird pricing?', a: ['Yes.'] },
      { q: 'Can we limit ticket quantities by ticket type?', a: ['Yes.'] },
      { q: 'Does the platform support waitlists when VIP lanes or reserved tables sell out?', a: ['Yes.'] },
      {
        q: 'Can guests easily upgrade from General Admission to VIP if availability opens?',
        a: ['Yes — upgrade prompts surface at checkout and post-purchase when availability opens.'],
      },
    ],
  },
  {
    title: 'Marketing & Growth',
    items: [
      {
        q: 'Does EventBrella offer any event discovery, or is traffic generated entirely through our own marketing efforts?',
        a: ['Traffic is generated through your own marketing efforts.'],
      },
      { q: 'Are there built-in email marketing tools for attendees?', a: ['Email marketing is customized to fit each event.'] },
      { q: 'Can we send SMS notifications before and after events?', a: ['Yes.'] },
      {
        q: 'Does the platform integrate with Meta Pixel, Google Analytics, or other advertising tools?',
        a: ['Yes. We understand the value of data in driving revenue and improving marketing allocation, and the platform is built to support that.'],
      },
      {
        q: 'Can we track where ticket sales originate (Instagram, Facebook, email, QR code, etc.)?',
        a: ['Yes. Every data point is used to make each successive event more profitable and to guide where your marketing dollars are best spent.'],
      },
    ],
  },
  {
    title: 'Customer Experience',
    items: [
      {
        q: 'Can guests reserve bowling lanes, tables, or experiences directly during checkout?',
        a: ['Yes — reservation is bundled into the same ticket checkout.'],
      },
      {
        q: 'Can guests edit their reservations after purchasing?',
        a: ["Yes, up to a set deadline — this prevents last-minute changes that haven't been approved by your team."],
      },
      {
        q: 'Is there a mobile app or digital wallet integration for tickets?',
        a: ['Tickets are mobile-based (QR pass); no separate app download required.'],
      },
      { q: 'Can attendees transfer tickets to someone else if they cannot attend?', a: ['Yes.'] },
    ],
  },
  {
    title: 'Reporting & Analytics',
    items: [
      {
        q: 'What reporting is available for ticket sales, attendance, revenue, and customer demographics?',
        a: ['Ticket sales, attendance, and revenue reporting are available.'],
      },
      { q: 'Can reports be exported to Excel or CSV?', a: ['Yes.'] },
      {
        q: 'Can we identify repeat attendees and our most loyal customers?',
        a: ['Yes — this is a core part of the platform (guest recognition across visits).'],
      },
      {
        q: 'Does the platform provide insights into purchasing trends and customer behavior?',
        a: ['Yes, at a guest and event level.'],
      },
    ],
  },
  {
    title: 'Integrations',
    items: [
      {
        q: 'Does EventBrella integrate with Constant Contact, Mailchimp, Zapier, Follow Up Boss, or other CRM platforms?',
        a: ['Yes.'],
      },
      { q: 'Are APIs available for custom integrations?', a: ['Yes.'] },
      { q: 'Can attendee information automatically sync with our CRM?', a: ['Yes.'] },
    ],
  },
  {
    title: 'Payments',
    items: [
      { q: 'What payment processors are supported?', a: ['All payment processing is handled via Stripe.'] },
      { q: 'How quickly are funds deposited after ticket sales?', a: ['Within 7 days.'] },
      {
        q: 'What are the platform and processing fees compared to Eventbrite?',
        a: ['We reduce fees by 50% for both hosts and guests compared to Eventbrite.'],
      },
    ],
  },
  {
    title: 'Operations',
    items: [
      {
        q: 'Can multiple team members have different permission levels (Admin, Check-In, Marketing, Finance)?',
        a: ['Yes.'],
      },
      { q: 'Can we scan tickets from multiple devices simultaneously?', a: ['Yes.'] },
      {
        q: 'Does the platform support offline check-in if internet service is unavailable?',
        a: ['Yes.'],
      },
      {
        q: 'Can refunds, exchanges, and ticket transfers be managed directly within the platform?',
        a: ['Yes.'],
      },
    ],
  },
  {
    title: 'Future Growth',
    items: [
      {
        q: 'As Strike After Dark expands to multiple venues and cities, can EventBrella support multiple locations, recurring events, sponsorship packages, memberships, and loyalty rewards?',
        a: [
          "Yes — this is where we excel. It's the reason behind the name EventBrella: bringing all things hospitality under one digital umbrella.",
        ],
      },
      {
        q: 'Are there any upcoming features or enhancements on the roadmap that would benefit our business?',
        a: [
          'As the platform grows, we gather more data from your guests — reviews and buying behavior, peak vs. slow purchasing times, which guests are likely to attend which events, who your VIPs are, and what your VIPs tend to order at your events.',
        ],
      },
    ],
  },
];

const EventBrellaFaq = () => (
  <EventBrellaLayout>
    <section className="eb-faq">
      <div className="wrap">
        <div className="section-head">
          <span className="eyebrow">FAQ</span>
          <h2>EventBrella FAQ</h2>
        </div>
        {FAQ_SECTIONS.map((section) => (
          <div className="eb-faq-section" key={section.title}>
            <h3>{section.title}</h3>
            {section.items.map((item) => (
              <details className="eb-faq-item" key={item.q}>
                <summary>{item.q}</summary>
                <div className="eb-faq-answer">
                  {item.a.map((paragraph) => (
                    <p key={paragraph.slice(0, 40)}>{paragraph}</p>
                  ))}
                </div>
              </details>
            ))}
          </div>
        ))}
      </div>
    </section>
  </EventBrellaLayout>
);

export default EventBrellaFaq;
